/* eslint-disable import/prefer-default-export */
import React from "react";
import { ArrowRight2 } from "iconsax-react";
import styled from "styled-components";

import { CATEGORY_COLORS, CATEGORY_LABELS } from "../../constants";
import { useMapUi } from "../../hooks/useMapUi";
import type { MapPoi } from "../../services/map.types";
import { isPoiNearbyToUser } from "../../services/mapService";
import { CategoryIcon } from "../../utils/categoryIcons";
import { PoiNearbyTag } from "../PoiNearbyTag";

const Card = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(100%, 320px);
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.md3Level2};
  cursor: pointer;
  text-align: left;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: 19px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IconWrap = styled.div<{ $bg: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
`;

const CategoryLabel = styled.span<{ $color: string }>`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ $color }) => $color};
`;

type CardDetailsPoiProps = {
  poi: MapPoi;
  userLatitude?: number;
  userLongitude?: number;
  onPress: () => void;
  onNearbyTagPress?: () => void;
};

export function CardDetailsPoi({
  poi,
  userLatitude,
  userLongitude,
  onPress,
  onNearbyTagPress,
}: CardDetailsPoiProps) {
  const mapUi = useMapUi();
  const categoryColor = CATEGORY_COLORS[poi.category];
  const isNearby = isPoiNearbyToUser(poi, userLatitude, userLongitude);

  return (
    <Card type="button" onClick={onPress}>
      <Title>{poi.name}</Title>
      {isNearby ? (
        <button
          type="button"
          aria-label="Perto de você"
          onClick={(e) => {
            e.stopPropagation();
            (onNearbyTagPress ?? onPress)();
          }}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <PoiNearbyTag accentColor={categoryColor} />
        </button>
      ) : null}
      <Footer>
        <IconWrap $bg={`${categoryColor}18`}>
          <CategoryIcon
            category={poi.category}
            size={28}
            color={mapUi.accent}
            variant="Bold"
          />
        </IconWrap>
        <ArrowRight2 size={20} color="var(--color-text-secondary, #6E6A7C)" />
      </Footer>
      <CategoryLabel $color={categoryColor}>
        {CATEGORY_LABELS[poi.category]}
      </CategoryLabel>
    </Card>
  );
}
